/**
 * @file index.tsx
 *
 * @fileoverview Renders layout's side block menu item
 *  or bottom menu item.
 */

import { MenuItem } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import { getTranslatedLink, isUrlActive } from '~/jobiqo/utils/links';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { PathContext } from '~/jobiqo/context/app';
import React, { useContext } from 'react';
import { LayoutSidebarSubMenu } from '~/jobiqo/components/Layout/components/SidebarSubMenu';
import { LayoutBottomSubMenu } from '~/jobiqo/components/Layout/components/BottomSubMenu';
import { Ripple } from '~/vcl/atoms/21-ripple';
import { HomeIcon } from '~/vcl/icons/Home';
import BriefcaseIcon from '~/vcl/icons/Briefcase';
import { GraduationCapIcon } from '~/vcl/icons/GraduationCap';
import UserAddIcon from '~/vcl/icons/UserAdd';
import { JobPerTemplateIcon } from '~/vcl/icons/JobPerTemplate';
import { OfficeIcon } from '~/vcl/icons/Office';
import { InvoiceIcon } from '~/vcl/icons/Invoice';
import { StarIcon } from '~/vcl/icons/Star';
import { ExploreIcon } from '~/vcl/icons/Explore';
import { EnvelopeIcon } from '~/vcl/icons/Envelope';
import { MenuIcon } from '~/vcl/icons/Menu';
import { SideBlockMenuItemWrapper } from '~/vcl/organisms/07-navigation/02-side-block/components/SideBlockMenuItemWrapper';
import { MenuLink } from '~/vcl/atoms/08-navigation/01-menu-link';
import { HiddenVisually as VisuallyHidden } from '~/vcl/atoms/14-w-a11y/02-visually-hidden';
import Image from '~/jobiqo/components/Image';
import NextLink from 'next/link';

/**
 * Returns appropriate icon for the given class.
 *
 * @param classItem
 * @returns
 *  The appropriate icon component.
 */
export const getMenuIcon = (classItem) => {
  switch (classItem) {
    case 'side-icon-home':
      return HomeIcon;
    case 'side-icon-applications':
      return BriefcaseIcon;
    case 'side-icon-resume':
      return GraduationCapIcon;
    case 'side-icon-resume-contact':
      return UserAddIcon;
    case 'side-icon-jobs':
      return JobPerTemplateIcon;
    case 'side-icon-company':
      return OfficeIcon;
    case 'side-icon-invoice':
      return InvoiceIcon;
    case 'side-icon-bookmark-jobs':
      return StarIcon;
    case 'side-icon-job-recommendations':
      return ExploreIcon;
    case 'side-icon-newsletters':
      return EnvelopeIcon;
    default:
      return MenuIcon;
  }
};

/**
 * Renders the icon for the menu item.
 *
 * @param menuItem
 *  The menu item.
 * @param position
 *  The menu item position.
 * @param LinkIcon
 *  The link icon component.
 * @param onClick
 *  Optional click handler for the icon.
 * @returns
 *  The icon component or image if one was uploaded by admins.
 */
export function renderIcon(
  menuItem: MenuItem,
  position: 'side' | 'bottom',
  LinkIcon: React.ComponentType<any>,
  onClick?: () => void,
) {
  if (menuItem.icon?.url) {
    return (
      <Image
        src={menuItem.icon.url}
        width={position === 'side' ? 25 : 20}
        height={position === 'side' ? 25 : 20}
        title={menuItem.title}
        alt={menuItem.title}
        {...(onClick && { onClick })}
      />
    );
  }

  if (LinkIcon) {
    return (
      <LinkIcon
        width={position === 'side' ? 25 : 20}
        height={position === 'side' ? 25 : 20}
        title={menuItem.title}
        {...(onClick && { onClick })}
      />
    );
  }

  return null;
}

interface LayoutMenuItemWrapperProps {
  /**
   * The menu item.
   */
  menuItem: MenuItem;
  /**
   * The menu item position.
   */
  position: 'side' | 'bottom';
}

export function LayoutMenuItemWrapper({
  menuItem,
  position,
}: LayoutMenuItemWrapperProps) {
  const pathContext: PathContextInterface = useContext(PathContext);
  const appContext: AppContextInterface = useContext(AppContext);

  const LinkIcon = getMenuIcon(menuItem.class);

  if (position === 'side') {
    return !!menuItem.children?.length ? (
      <LayoutSidebarSubMenu menuItem={menuItem} />
    ) : (
      <SideBlockMenuItemWrapper
        active={isUrlActive(menuItem.url, pathContext.path)}
        key={`${menuItem.url.path}--${menuItem.title}`}
      >
        <Link
          href={menuItem.url.path}
          as={getTranslatedLink(menuItem.url.path, appContext.locale)}
        >
          <>
            {renderIcon(menuItem, position, LinkIcon)}
            <span>{menuItem.title}</span>
          </>
        </Link>
      </SideBlockMenuItemWrapper>
    );
  }

  if (position === 'bottom') {
    return !!menuItem.children?.length ? (
      <LayoutBottomSubMenu menuItem={menuItem} />
    ) : (
      <Link
        asChild
        key={`${menuItem.url.path}--${menuItem.title}`}
        href={menuItem.url.path}
      >
        <MenuLink
          linkElement={NextLink}
          href={menuItem.url.path}
          active={isUrlActive(menuItem.url, pathContext.path)}
        >
          <Ripple backgroundColor="primary" />
          {renderIcon(menuItem, position, LinkIcon)}
          <VisuallyHidden>
            <span>{menuItem.title}</span>
          </VisuallyHidden>
        </MenuLink>
      </Link>
    );
  }

  return null;
}

export default LayoutMenuItemWrapper;
